import { useEffect, useMemo, useState } from 'react';

import { getVirtualTourStatusesApi } from '../../util/api';

const CACHE_TTL_MS = 60 * 1000;
const availabilityCache = new Map();
const pendingRequests = new Map();

const listingId = listing => listing?.id?.uuid || listing?.id;

const cachedAvailability = id => {
  const cached = availabilityCache.get(id);
  return cached && cached.expiresAt > Date.now() ? cached.value : undefined;
};

const loadAvailabilities = ids => {
  const missingIds = ids.filter(id => cachedAvailability(id) === undefined);
  if (missingIds.length === 0) {
    return Promise.resolve(Object.fromEntries(ids.map(id => [id, cachedAvailability(id)])));
  }

  const requestKey = [...missingIds].sort().join(',');
  let request = pendingRequests.get(requestKey);
  if (!request) {
    request = getVirtualTourStatusesApi(missingIds)
      .then(response => {
        const availability = response?.hasVirtualTourByListingId || {};
        const expiresAt = Date.now() + CACHE_TTL_MS;
        missingIds.forEach(id => {
          availabilityCache.set(id, { value: availability[id] === true, expiresAt });
        });
      })
      .finally(() => pendingRequests.delete(requestKey));
    pendingRequests.set(requestKey, request);
  }

  return request.then(() =>
    Object.fromEntries(ids.map(id => [id, cachedAvailability(id) === true]))
  );
};

const useVirtualTourAvailabilities = listings => {
  const idsKey = useMemo(
    () => [...new Set((listings || []).map(listingId).filter(Boolean))].sort().join(','),
    [listings]
  );
  const ids = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);
  const [availability, setAvailability] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (ids.length === 0) {
      setAvailability({});
      return () => {
        cancelled = true;
      };
    }

    loadAvailabilities(ids)
      .then(result => {
        if (!cancelled) setAvailability(result);
      })
      .catch(() => {
        if (!cancelled) setAvailability({});
      });

    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return availability;
};

export default useVirtualTourAvailabilities;
